import {
  ChevronRight,
  ChevronsUpDown,
  GripVertical,
  Plus,
  X
} from "lucide-react";
import { useState, type FC } from "react";

import type {
  OutgoingIntakeFormNode,
  OutgoingIntakeQuestionType
} from "@/api/types/outgoingIntake";
import { Button } from "@/components/ui/button";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger
} from "@/components/ui/collapsible";
import {
  Field,
  FieldDescription,
  FieldGroup,
  FieldLabel
} from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { SaveToPicker } from "@/features/requestsIde/components/SaveToPicker";
import { ShowIfField } from "@/features/requestsIde/editor/ShowIfField";
import { slugify, stepLabel } from "@/features/requestsIde/editor/stepModel";
import { savesToOf, useSaveTargets } from "@/features/requestsIde/saveTargets";
import { cn } from "@/lib/utils";

import { QUESTION_TYPES, hasOptions, typeOf } from "../model";

type NodeFieldsProps = {
  node: OutgoingIntakeFormNode;
  /** The whole intake, for conditions on earlier answers. */
  schema: OutgoingIntakeFormNode[];
  /** "Mariana", or "The client" on a template. */
  who: string;
  readOnly: boolean;
  onChange: (patch: Partial<OutgoingIntakeFormNode>) => void;
};

type QuestionFieldsProps = NodeFieldsProps & {
  /** Inside a list: no condition of its own. */
  nested?: boolean;
};

const possessive = (who: string) =>
  who.toLowerCase() === "the client" ? "the client's" : `${who}'s`;

/**
 * Where the answer is written on accept. Folded under "Advanced": most
 * questions map themselves, and the choice is made again at Accept and File.
 */
export const AdvancedSaveTo: FC<{
  node: OutgoingIntakeFormNode;
  who: string;
  readOnly: boolean;
  onChange: (patch: Partial<OutgoingIntakeFormNode>) => void;
}> = ({ node, who, readOnly, onChange }) => {
  const targets = useSaveTargets();
  const saves = savesToOf(node, targets, stepLabel(node));
  const whose = possessive(who);
  const fromTemplate =
    Boolean(node.model_path) && node.template_source !== "custom";
  return (
    <Collapsible>
      <CollapsibleTrigger className="group flex items-center gap-1 text-sm text-fg-3 hover:text-fg">
        <ChevronRight className="size-3 transition-transform group-data-[state=open]:rotate-90" />
        Advanced
      </CollapsibleTrigger>
      <CollapsibleContent className="pt-3">
        <Field>
          <FieldLabel className="text-sm">Save Answer To</FieldLabel>
          {saves.kind === "vault" ? (
            <div className="flex h-8 items-center rounded-md bg-fill-1 px-2.5 text-base text-fg-3">
              {saves.label}
            </div>
          ) : (
            <SaveToPicker
              value={saves.kind === "record" ? saves.path : null}
              questionType={node.question_type}
              onPick={(path) => onChange({ model_path: path })}
            >
              <Button
                variant="secondary"
                size="md"
                disabled={readOnly || fromTemplate}
                className="w-full justify-between"
              >
                <span className={saves.kind === "none" ? "text-fg-3" : ""}>
                  {saves.kind === "record"
                    ? saves.label
                    : "Keep on the submission"}
                </span>
                <ChevronsUpDown className="text-fg-3" />
              </Button>
            </SaveToPicker>
          )}
          <FieldDescription className="text-sm">
            {saves.kind === "vault"
              ? `Filed to ${whose} documents when you accept it.`
              : saves.kind === "record"
              ? fromTemplate
                ? `Written to ${whose} profile when you accept it. Answer type and choices are fixed.`
                : `Written to ${whose} profile when you accept it.`
              : "You can still save it to the profile when you accept the answers."}
          </FieldDescription>
        </Field>
      </CollapsibleContent>
    </Collapsible>
  );
};

const Choices: FC<{
  node: OutgoingIntakeFormNode;
  locked: boolean;
  onChange: (patch: Partial<OutgoingIntakeFormNode>) => void;
}> = ({ node, locked, onChange }) => {
  const [dragFrom, setDragFrom] = useState<number | null>(null);
  const options = node.options ?? [];
  const move = (from: number, to: number) => {
    if (from === to) return;
    const next = options.slice();
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    onChange({ options: next });
  };
  const setLabel = (index: number, label: string) => {
    const next = options.slice();
    next[index] = {
      option_id: next[index]?.option_id || slugify(label) || `opt_${index + 1}`,
      label
    };
    onChange({ options: next });
  };
  return (
    <Field>
      <FieldLabel className="text-sm">Choices</FieldLabel>
      <div className="flex flex-col gap-1.5">
        {options.map((o, i) => (
          <div
            key={`${o.option_id}-${i}`}
            className={cn(
              "flex items-center gap-1.5 rounded-md",
              dragFrom !== null &&
                dragFrom !== i &&
                "data-[over=true]:bg-fill-1"
            )}
            onDragOver={(e) => {
              if (dragFrom === null) return;
              e.preventDefault();
              e.currentTarget.dataset.over = "true";
            }}
            onDragLeave={(e) => {
              e.currentTarget.dataset.over = "false";
            }}
            onDrop={(e) => {
              e.currentTarget.dataset.over = "false";
              if (dragFrom !== null) move(dragFrom, i);
              setDragFrom(null);
            }}
          >
            {!locked && options.length > 1 ? (
              <span
                draggable
                role="button"
                tabIndex={0}
                aria-label={`Move “${o.label || "choice"}”. Use arrow keys.`}
                onDragStart={(e) => {
                  e.dataTransfer.effectAllowed = "move";
                  setDragFrom(i);
                }}
                onDragEnd={() => setDragFrom(null)}
                onKeyDown={(e) => {
                  if (e.key === "ArrowUp" && i > 0) {
                    e.preventDefault();
                    move(i, i - 1);
                  } else if (e.key === "ArrowDown" && i < options.length - 1) {
                    e.preventDefault();
                    move(i, i + 1);
                  }
                }}
                className="cursor-grab rounded-sm text-fg-4 outline-none hover:text-fg focus-visible:ring-2 focus-visible:ring-primary/40 active:cursor-grabbing"
              >
                <GripVertical className="size-3.5" />
              </span>
            ) : (
              <span className="w-3.5" />
            )}
            <Input
              value={o.label}
              aria-label={`Choice ${i + 1}`}
              aria-invalid={!String(o.label ?? "").trim() || undefined}
              placeholder={`Choice ${i + 1} needs a label`}
              disabled={locked}
              onChange={(e) => setLabel(i, e.target.value)}
              className="h-7"
            />
            {!locked ? (
              <Button
                variant="ghost"
                size="icon-xs"
                aria-label="Remove Choice"
                onClick={() =>
                  onChange({ options: options.filter((_, j) => j !== i) })
                }
              >
                <X className="size-3" />
              </Button>
            ) : null}
          </div>
        ))}
        {!locked ? (
          <Button
            variant="ghost"
            size="xs"
            className="w-fit"
            onClick={() =>
              onChange({
                options: [
                  ...options,
                  { option_id: `opt_${options.length + 1}`, label: "" }
                ]
              })
            }
          >
            <Plus className="size-3" /> Add Choice
          </Button>
        ) : null}
      </div>
    </Field>
  );
};

/** A question's own fields: wording, type, required, choices, condition, save target. */
export const QuestionFields: FC<QuestionFieldsProps> = ({
  node,
  schema,
  who,
  readOnly,
  onChange,
  nested
}) => {
  const fixed = Boolean(node.model_path) && node.template_source !== "custom";
  return (
    <FieldGroup className="gap-3">
      <Field>
        <FieldLabel htmlFor={`prompt-${node.node_id}`} className="text-sm">
          Question
        </FieldLabel>
        <Textarea
          id={`prompt-${node.node_id}`}
          value={node.prompt ?? ""}
          disabled={readOnly}
          rows={1}
          onChange={(e) => onChange({ prompt: e.target.value })}
          className="min-h-0 text-base"
        />
      </Field>
      <Field>
        <FieldLabel htmlFor={`help-${node.node_id}`} className="text-sm">
          Help Text
        </FieldLabel>
        <Textarea
          id={`help-${node.node_id}`}
          value={node.help_text ?? ""}
          disabled={readOnly}
          rows={1}
          placeholder="Shown under the question. One sentence."
          onChange={(e) => onChange({ help_text: e.target.value || null })}
          className="min-h-0 text-base"
        />
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field>
          <FieldLabel className="text-sm">Answer Type</FieldLabel>
          <Select
            value={node.question_type ?? "short_text"}
            disabled={readOnly || fixed}
            onValueChange={(v) =>
              onChange({ question_type: v as OutgoingIntakeQuestionType })
            }
          >
            <SelectTrigger size="sm" className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {QUESTION_TYPES.map((t) => (
                <SelectItem key={t.value} value={t.value}>
                  <t.icon className="size-3.5 text-fg-3" />
                  {t.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
        <Field>
          <FieldLabel className="text-sm">Required</FieldLabel>
          <div className="flex h-7 items-center gap-2 text-base text-fg-2">
            <Switch
              checked={Boolean(node.required)}
              disabled={readOnly}
              aria-label="Required"
              onCheckedChange={(v) => onChange({ required: v })}
            />
            {node.required ? `${who} must answer` : `${who} can skip it`}
          </div>
        </Field>
      </div>
      {hasOptions(node.question_type) ? (
        <Choices node={node} locked={readOnly || fixed} onChange={onChange} />
      ) : null}
      {nested ? null : (
        <ShowIfField
          node={node}
          schema={schema}
          readOnly={readOnly}
          onChange={(show_if) => onChange({ show_if })}
        />
      )}
      <AdvancedSaveTo
        node={node}
        who={who}
        readOnly={readOnly}
        onChange={onChange}
      />
      {node.uscis_form_number ? (
        <p className="text-sm text-fg-3">
          USCIS {node.uscis_form_number}
          {node.uscis_schema_field ? ` · ${node.uscis_schema_field}` : ""}
        </p>
      ) : null}
    </FieldGroup>
  );
};

/** A list (repeating step): its title, how many entries, and what each asks. */
export const GroupFields: FC<NodeFieldsProps> = ({
  node,
  schema,
  who,
  readOnly,
  onChange
}) => {
  const [editing, setEditing] = useState<string | null>(null);
  const children = node.children ?? [];
  const setChild = (id: string, patch: Partial<OutgoingIntakeFormNode>) =>
    onChange({
      children: children.map((c) => (c.node_id === id ? { ...c, ...patch } : c))
    });
  const addChild = () => {
    const id = `${node.node_id}.custom_${children.length + 1}`;
    onChange({
      children: [
        ...children,
        {
          node_id: id,
          node_type: "question",
          prompt: "New sub-question",
          question_type: "short_text",
          required: false,
          options: [],
          template_source: "custom",
          children: []
        }
      ]
    });
    setEditing(id);
  };
  const editingNode = children.find((c) => c.node_id === editing) ?? null;
  return (
    <FieldGroup className="gap-3">
      <Field>
        <FieldLabel htmlFor={`title-${node.node_id}`} className="text-sm">
          Title
        </FieldLabel>
        <Input
          id={`title-${node.node_id}`}
          value={node.title ?? ""}
          disabled={readOnly}
          onChange={(e) => onChange({ title: e.target.value })}
        />
        <FieldDescription className="text-sm">
          {who} sees this as one step and adds an entry per item.
        </FieldDescription>
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field>
          <FieldLabel className="text-sm">At Least</FieldLabel>
          <Input
            type="number"
            min={0}
            value={node.min_items ?? ""}
            disabled={readOnly}
            onChange={(e) =>
              onChange({
                min_items: e.target.value === "" ? null : Number(e.target.value)
              })
            }
          />
        </Field>
        <Field>
          <FieldLabel className="text-sm">At Most</FieldLabel>
          <Input
            type="number"
            min={1}
            placeholder="No limit"
            value={node.max_items ?? ""}
            disabled={readOnly}
            onChange={(e) =>
              onChange({
                max_items: e.target.value === "" ? null : Number(e.target.value)
              })
            }
          />
        </Field>
      </div>
      <Field>
        <FieldLabel className="text-sm">Asked for Each Entry</FieldLabel>
        <div className="overflow-hidden rounded-lg ring-1 ring-line-2">
          {children.length ? (
            <div className="divide-y divide-line-1">
              {children.map((c, i) => {
                const t = typeOf(c);
                return (
                  <button
                    key={c.node_id}
                    type="button"
                    onClick={() =>
                      setEditing(editing === c.node_id ? null : c.node_id)
                    }
                    className={cn(
                      "flex h-8 w-full items-center gap-2.5 px-2.5 text-left text-base hover:bg-fill-1",
                      editing === c.node_id && "bg-fill-1"
                    )}
                  >
                    <span className="w-3 text-sm text-fg-4">
                      {String.fromCharCode(97 + i)}
                    </span>
                    <t.icon className="size-3.5 text-fg-3" />
                    <span className="min-w-0 flex-1 truncate">
                      {stepLabel(c)}
                    </span>
                    <span className="text-sm text-fg-3">{t.label}</span>
                  </button>
                );
              })}
            </div>
          ) : (
            <p className="px-2.5 py-2 text-sm text-fg-3">Nothing asked yet.</p>
          )}
        </div>
        {!readOnly ? (
          <Button
            variant="ghost"
            size="xs"
            className="w-fit"
            onClick={addChild}
          >
            <Plus className="size-3" /> Add Sub-Question
          </Button>
        ) : null}
      </Field>
      {editingNode ? (
        <div className="flex flex-col gap-3 rounded-lg bg-fill-1 p-3">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-fg-2">Sub-Question</span>
            {!readOnly ? (
              <Button
                variant="danger"
                size="xs"
                onClick={() => {
                  onChange({
                    children: children.filter(
                      (c) => c.node_id !== editingNode.node_id
                    )
                  });
                  setEditing(null);
                }}
              >
                Remove
              </Button>
            ) : null}
          </div>
          <QuestionFields
            nested
            node={editingNode}
            schema={schema}
            who={who}
            readOnly={readOnly}
            onChange={(patch) => setChild(editingNode.node_id, patch)}
          />
        </div>
      ) : null}
      <ShowIfField
        node={node}
        schema={schema}
        readOnly={readOnly}
        onChange={(show_if) => onChange({ show_if })}
      />
    </FieldGroup>
  );
};

/** The right fields for whichever step this is. */
export const StepFields: FC<NodeFieldsProps> = ({ node, ...rest }) =>
  node.node_type === "repeatable_group" ? (
    <GroupFields node={node} {...rest} />
  ) : (
    <QuestionFields node={node} {...rest} />
  );
